import styled from '@emotion/styled';
import { border_radius_styles_with_size, semantic_colors, semantic_spacing, text_styles } from '../../foundation';
import { buildCSSWithLength, getAutoColorCode } from '../../styles';
import { COLUMN_MIN_HEIGHT } from './constants';
import { CellSize, ColumnAlign } from './types';

const TABLE_CONTAINER_BORDER_WIDTH = 1;
const TABLE_HEADER_Z_INDEX = 2;
const TABLE_FIXED_HEADER_Z_INDEX = 3;
const TABLE_FLOATING_HEADER_Z_INDEX = 4;

const getFloatingHeaderOuterWidth = (containerWidth: number) =>
  containerWidth > 0 ? `calc(${containerWidth}px + ${TABLE_CONTAINER_BORDER_WIDTH * 2}px)` : 0;

const getFloatingHeaderOuterHeight = (headerHeight: number) =>
  headerHeight > 0 ? headerHeight + TABLE_CONTAINER_BORDER_WIDTH : 0;

export const StyledTableCol = styled.th<{
  fixed?: boolean;
  fixedColWidth?: number;
  stickyHeader?: boolean;
  stickyOffset?: number;
  groupColumn?: boolean;
  sortable?: boolean;
  resize?: boolean;
  align?: ColumnAlign;
  valign?: 'middle' | 'top' | 'bottom' | 'baseline';
}>`
  background-color: ${semantic_colors.fill.tertiary};
  padding: 12px;
  ${buildCSSWithLength('height', COLUMN_MIN_HEIGHT)};
  z-index: ${TABLE_HEADER_Z_INDEX};
  vertical-align: ${({ valign }) => valign};
  ${text_styles.Body_13_SemiBold};
  color: ${semantic_colors.content.primary};
  text-align: ${({ align }) => align ?? 'left'};
  border: 1px solid ${semantic_colors.border.primary};
  transition: background-color 0.1s linear;
  ${({ stickyHeader, fixed }) => (stickyHeader || fixed) && `position: sticky;`};
  ${({ stickyHeader, fixed, groupColumn, stickyOffset = 0 }) =>
    (stickyHeader || fixed) && `${buildCSSWithLength('top', stickyOffset + (groupColumn ? COLUMN_MIN_HEIGHT : 0))};`};
  ${({ fixed, fixedColWidth }) =>
    fixed && `z-index: ${TABLE_FIXED_HEADER_Z_INDEX}; ${buildCSSWithLength('left', fixedColWidth ?? 0)};`};
  ${({ sortable }) => {
    const { hover, pressed } = getAutoColorCode(semantic_colors.fill.tertiary);
    return (
      sortable &&
      `
      cursor: pointer;
      &:hover {
        background-color: ${hover};
      }
      &:active {
        background-color: ${pressed};
      }
    `
    );
  }}
  ${({ resize }) =>
    resize &&
    `resize: horizontal; overflow: hidden;
    .ContentsWrapper {
    overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
  }`}
`;

export const cell_size_css: Record<CellSize, string> = {
  small: `
    padding: ${semantic_spacing.table.padding_small.desktop};
  `,
  medium: `
    padding: ${semantic_spacing.table.padding_medium.desktop};
  `,
  large: `
    padding: ${semantic_spacing.table.padding_large.desktop};
  `,
};

export const StickyHeaderHost = styled.div`
  position: relative;
  overflow: visible;
`;

export const FloatingHeader = styled.div<{
  $visible: boolean;
  $stickyOffset: number;
  $containerWidth: number;
  $headerHeight: number;
}>`
  position: sticky;
  ${({ $stickyOffset }) => buildCSSWithLength('top', $stickyOffset)};
  ${({ $containerWidth }) => buildCSSWithLength('width', getFloatingHeaderOuterWidth($containerWidth))};
  ${({ $headerHeight }) => buildCSSWithLength('height', getFloatingHeaderOuterHeight($headerHeight))};
  margin-bottom: ${({ $headerHeight }) => {
    const outerHeight = getFloatingHeaderOuterHeight($headerHeight);

    return outerHeight > 0 ? `-${outerHeight}px` : 0;
  }};
  box-sizing: border-box;
  background-color: ${semantic_colors.fill.tertiary};
  border: ${TABLE_CONTAINER_BORDER_WIDTH}px solid ${semantic_colors.border.primary};
  border-bottom: none;
  border-top-left-radius: ${border_radius_styles_with_size.small}px;
  border-top-right-radius: ${border_radius_styles_with_size.small}px;
  overflow: hidden;
  z-index: ${TABLE_FLOATING_HEADER_Z_INDEX};
  visibility: ${({ $visible }) => ($visible ? 'visible' : 'hidden')};
  pointer-events: ${({ $visible }) => ($visible ? 'auto' : 'none')};

  .pds-table-cell-fixed {
    transform: translateX(var(--pds-table-sticky-scroll-left, 0px));
  }
`;

export const TranslateInner = styled.div<{ $tableWidth: number }>`
  ${({ $tableWidth }) => buildCSSWithLength('width', $tableWidth)};
  will-change: transform;
`;

export const StickyHeaderSentinel = styled.div`
  height: 1px;
  margin-bottom: -1px;
  pointer-events: none;
`;
